import { useState, useCallback } from 'react'
import {
  supabase,
  ExtractType,
  KeyPoint,
  Flashcard,
  AISummary,
  DbSharedExtract,
  generateShareToken
} from '../lib/supabase'
import { Article } from './useArticles'

export interface SharedExtract {
  id: string
  articleId: string
  type: ExtractType
  content: {
    title: string
    extractContent: string | KeyPoint[] | Flashcard[]
    sourceTitle: string
    sourceUrl: string
    sourceExcerpt?: string
  }
  shareToken: string
  shareUrl: string
  viewCount: number
  isPublic: boolean
  expiresAt: string | null
  createdAt: string
}

interface UseAIExtractsReturn {
  loading: boolean
  error: string | null
  summary: AISummary | null
  generateSummary: (article: Article) => Promise<AISummary | null>
  generateKeyPoints: (article: Article) => Promise<KeyPoint[]>
  generateFlashcards: (article: Article) => Promise<Flashcard[]>
  shareExtract: (
    article: Article,
    type: ExtractType,
    content: string | KeyPoint[] | Flashcard[],
    title?: string
  ) => Promise<SharedExtract | null>
  getSharedExtract: (token: string) => Promise<SharedExtract | null>
  exportAsMarkdown: (extract: SharedExtract) => string
  exportForTwitter: (extract: SharedExtract) => string
  exportForLinkedIn: (extract: SharedExtract) => string
  copyToClipboard: (text: string) => Promise<boolean>
}

// Mock AI generation - replace with actual AI API calls
async function mockGenerateSummary(content: string, title: string): Promise<AISummary> {
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 1500))

  // Extract first few sentences for brief summary
  const sentences = content.split(/[.!?]+/).filter(s => s.trim().length > 0)
  const brief = sentences.slice(0, 2).join('. ').trim() + '.'

  // Create a mock detailed summary
  const detailed = sentences.slice(0, 5).join('. ').trim() + '.'

  // Extract key points (mock - in reality this would be AI-generated)
  const keyPoints: KeyPoint[] = sentences.slice(0, 5).map((s, i) => ({
    id: `kp-${i}`,
    text: s.trim(),
    importance: i === 0 ? 'high' : i < 3 ? 'medium' : 'low'
  }))

  // Generate mock flashcards
  const flashcards: Flashcard[] = [
    {
      id: 'fc-1',
      front: `What is the main topic of "${title}"?`,
      back: brief,
      tags: ['summary']
    },
    {
      id: 'fc-2',
      front: 'What are the key takeaways?',
      back: keyPoints.slice(0, 3).map(kp => `• ${kp.text}`).join('\n'),
      tags: ['key-points']
    }
  ]

  return {
    brief,
    detailed,
    keyPoints,
    flashcards,
    generatedAt: new Date().toISOString(),
    model: 'mock-v1' // Will be replaced with actual model name
  }
}

export function useAIExtracts(userId: string | undefined): UseAIExtractsReturn {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [summary, setSummary] = useState<AISummary | null>(null)

  const generateSummary = useCallback(async (article: Article): Promise<AISummary | null> => {
    if (!article.content) {
      setError('Article has no content to summarize')
      return null
    }

    setLoading(true)
    setError(null)

    try {
      const result = await mockGenerateSummary(article.content, article.title)
      setSummary(result)

      // Optionally save to article's ai_summary field
      if (userId) {
        await supabase
          .from('articles')
          .update({
            ai_summary: result,
            updated_at: new Date().toISOString()
          })
          .eq('id', article.id)
          .eq('user_id', userId)
      }

      return result
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to generate summary'
      setError(message)
      return null
    } finally {
      setLoading(false)
    }
  }, [userId])

  const generateKeyPoints = useCallback(async (article: Article): Promise<KeyPoint[]> => {
    const sum = summary || await generateSummary(article)
    return sum?.keyPoints || []
  }, [summary, generateSummary])

  const generateFlashcards = useCallback(async (article: Article): Promise<Flashcard[]> => {
    const sum = summary || await generateSummary(article)
    return sum?.flashcards || []
  }, [summary, generateSummary])

  const shareExtract = useCallback(async (
    article: Article,
    type: ExtractType,
    content: string | KeyPoint[] | Flashcard[],
    title?: string
  ): Promise<SharedExtract | null> => {
    if (!userId) {
      setError('Must be logged in to share')
      return null
    }

    setLoading(true)
    setError(null)

    try {
      const shareToken = generateShareToken()
      const baseUrl = window.location.origin

      const extractTitle = title || `${type.charAt(0).toUpperCase() + type.slice(1).replace('_', ' ')} - ${article.title}`

      const dbExtract: Omit<DbSharedExtract, 'id' | 'created_at' | 'updated_at'> = {
        user_id: userId,
        article_id: article.id,
        extract_type: type,
        content: {
          title: extractTitle,
          extractContent: content,
          sourceTitle: article.title,
          sourceUrl: article.url,
          sourceExcerpt: article.excerpt
        },
        share_token: shareToken,
        view_count: 0,
        is_public: true,
        expires_at: null
      }

      const { data, error: insertError } = await supabase
        .from('shared_extracts')
        .insert(dbExtract)
        .select()
        .single()

      if (insertError) {
        throw insertError
      }

      const shared = data as DbSharedExtract

      return {
        id: shared.id,
        articleId: shared.article_id,
        type: shared.extract_type,
        content: shared.content,
        shareToken: shared.share_token,
        shareUrl: `${baseUrl}/share/${shared.share_token}`,
        viewCount: shared.view_count,
        isPublic: shared.is_public,
        expiresAt: shared.expires_at,
        createdAt: shared.created_at
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create share link'
      setError(message)
      return null
    } finally {
      setLoading(false)
    }
  }, [userId])

  const getSharedExtract = useCallback(async (token: string): Promise<SharedExtract | null> => {
    try {
      const { data, error: queryError } = await supabase
        .from('shared_extracts')
        .select('*')
        .eq('share_token', token)
        .eq('is_public', true)
        .single()

      if (queryError) {
        throw queryError
      }

      if (!data) return null

      const shared = data as DbSharedExtract
      const baseUrl = window.location.origin

      // Increment view count
      await supabase
        .from('shared_extracts')
        .update({ view_count: shared.view_count + 1 })
        .eq('id', shared.id)

      return {
        id: shared.id,
        articleId: shared.article_id,
        type: shared.extract_type,
        content: shared.content,
        shareToken: shared.share_token,
        shareUrl: `${baseUrl}/share/${shared.share_token}`,
        viewCount: shared.view_count + 1,
        isPublic: shared.is_public,
        expiresAt: shared.expires_at,
        createdAt: shared.created_at
      }
    } catch (err) {
      console.error('Error fetching shared extract:', err)
      return null
    }
  }, [])

  const exportAsMarkdown = useCallback((extract: SharedExtract): string => {
    const { content, type } = extract
    let markdown = `# ${content.title}\n\n`

    if (type === 'summary' && typeof content.extractContent === 'string') {
      markdown += content.extractContent
    } else if (type === 'key_points' && Array.isArray(content.extractContent)) {
      markdown += '## Key Points\n\n'
      ;(content.extractContent as KeyPoint[]).forEach((kp, i) => {
        const importance = kp.importance === 'high' ? '**' : ''
        markdown += `${i + 1}. ${importance}${kp.text}${importance}\n`
      })
    } else if (type === 'flashcards' && Array.isArray(content.extractContent)) {
      markdown += '## Flashcards\n\n'
      ;(content.extractContent as Flashcard[]).forEach((fc, i) => {
        markdown += `### Card ${i + 1}\n\n`
        markdown += `**Q:** ${fc.front}\n\n`
        markdown += `**A:** ${fc.back}\n\n`
        if (fc.tags?.length) {
          markdown += `*Tags: ${fc.tags.join(', ')}*\n\n`
        }
        markdown += '---\n\n'
      })
    }

    markdown += `\n---\n\n`
    markdown += `*Source: [${content.sourceTitle}](${content.sourceUrl})*\n\n`
    markdown += `*Generated by [Read Later AI](${window.location.origin})*`

    return markdown
  }, [])

  const exportForTwitter = useCallback((extract: SharedExtract): string => {
    const { content, shareUrl } = extract
    const maxLength = 280 - shareUrl.length - 5 // Account for URL and spacing

    let text = ''
    if (typeof content.extractContent === 'string') {
      text = content.extractContent.slice(0, maxLength)
      if (content.extractContent.length > maxLength) {
        text = text.slice(0, text.lastIndexOf(' ')) + '...'
      }
    } else if (Array.isArray(content.extractContent) && content.extractContent.length > 0) {
      const firstItem = content.extractContent[0]
      if ('text' in firstItem) {
        text = `Key insight: ${(firstItem as KeyPoint).text}`
      } else if ('front' in firstItem) {
        text = (firstItem as Flashcard).front
      }
      text = text.slice(0, maxLength)
    }

    return `${text}\n\n${shareUrl}`
  }, [])

  const exportForLinkedIn = useCallback((extract: SharedExtract): string => {
    const { content, shareUrl } = extract

    let text = `📚 ${content.title}\n\n`

    if (typeof content.extractContent === 'string') {
      text += content.extractContent.slice(0, 500)
      if (content.extractContent.length > 500) {
        text += '...'
      }
    } else if (Array.isArray(content.extractContent)) {
      if (content.extractContent.length > 0 && 'text' in content.extractContent[0]) {
        text += 'Key takeaways:\n\n'
        ;(content.extractContent as KeyPoint[]).slice(0, 3).forEach((kp, i) => {
          text += `${i + 1}. ${kp.text}\n`
        })
      }
    }

    text += `\n\nRead more: ${shareUrl}\n\n`
    text += `#ReadLater #Learning #KnowledgeManagement`

    return text
  }, [])

  const copyToClipboard = useCallback(async (text: string): Promise<boolean> => {
    try {
      await navigator.clipboard.writeText(text)
      return true
    } catch (err) {
      console.error('Failed to copy:', err)
      return false
    }
  }, [])

  return {
    loading,
    error,
    summary,
    generateSummary,
    generateKeyPoints,
    generateFlashcards,
    shareExtract,
    getSharedExtract,
    exportAsMarkdown,
    exportForTwitter,
    exportForLinkedIn,
    copyToClipboard
  }
}
